import { useEffect, useState, useCallback } from "react";
import { adminApi } from "@/lib/adminApi";
import { fmtDate } from "@/pages/admin/util";
import { Loader2, RefreshCw, Play, Ban, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["", "PENDING", "SENT", "SKIPPED", "CANCELLED", "FAILED"];

const STATUS_TONE = {
  PENDING:   "bg-[#B88A2A]/15 text-[#B88A2A]",
  SENT:      "bg-[#064E2E]/10 text-[#064E2E]",
  SKIPPED:   "bg-[#475569]/15 text-[#475569]",
  CANCELLED: "bg-[#475569]/15 text-[#475569]",
  FAILED:    "bg-[#DC2626]/10 text-[#DC2626]",
};

export default function Automation() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("PENDING");
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/automation-tasks", { params: status ? { status } : {} });
      setItems(data.items);
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  async function runDue() {
    setRunning(true);
    try {
      const { data } = await adminApi.post("/automation/run-due");
      toast.success(`Processed ${data.processed} · sent ${data.sent} · skipped ${data.skipped} · failed ${data.failed}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Run failed");
    } finally { setRunning(false); }
  }

  async function cancel(id) {
    if (!window.confirm("Cancel this pending task?")) return;
    try {
      await adminApi.post(`/automation-tasks/${id}/cancel`);
      toast.success("Task cancelled");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cancel failed");
    }
  }

  return (
    <div data-testid="admin-automation-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="eyebrow">Follow-up engine</span>
          <h1 className="font-display text-[32px] lg:text-[40px] leading-[1.05] tracking-tight text-[#071A33] mt-2">Automation queue</h1>
          <p className="text-sm text-[#475569] mt-1">
            Scheduled email + SMS follow-ups for quote reviews. Tasks auto-cancel when a contact converts to ACTIVE member.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select className="field-input h-10" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="auto-status-filter">
            {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
          </select>
          <button onClick={load} className="btn-outline h-10 py-0 px-4"><RefreshCw size={14}/></button>
          <button onClick={runDue} disabled={running} className="btn-primary h-10 py-0 px-4 whitespace-nowrap" data-testid="auto-run-due">
            {running ? <Loader2 className="animate-spin" size={14}/> : <><Play size={14}/> Run due now</>}
          </button>
        </div>
      </div>

      <div className="mt-6 admin-panel overflow-x-auto">
        <table className="w-full text-sm" data-testid="automation-table">
          <thead>
            <tr className="bg-[#F7F4ED] text-left text-[#475569] text-xs uppercase tracking-[0.15em]">
              <th className="px-4 py-3">Step</th>
              <th className="px-4 py-3">Channel</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Case</th>
              <th className="px-4 py-3">Scheduled</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Sent</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-8 text-center text-[#475569]"><Loader2 className="animate-spin inline" size={16}/></td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-[#475569]">No tasks.</td></tr>}
            {items.map((t) => (
              <tr key={t.id} className="border-t border-[#E2DCD0]">
                <td className="px-4 py-3 font-medium text-[#071A33]">#{t.step}</td>
                <td className="px-4 py-3 text-[#475569]">
                  <span className="inline-flex items-center gap-1.5">
                    {t.channel === "EMAIL" ? <Mail size={14}/> : <MessageCircle size={14}/>}
                    {t.channel}
                  </span>
                </td>
                <td className="px-4 py-3 text-[#475569]">
                  <div>{t.full_name || "—"}</div>
                  <div className="text-[11px]">{t.email} {t.phone ? `· ${t.phone}` : ""}</div>
                </td>
                <td className="px-4 py-3 text-[#475569] font-mono text-[11px]">{t.case_id?.slice(0, 8)}…</td>
                <td className="px-4 py-3 text-[#475569]">{fmtDate(t.scheduled_at)}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 text-[10px] font-medium tracking-[0.1em] uppercase ${STATUS_TONE[t.status] || "bg-[#E2DCD0]"}`}>
                    {t.status}
                  </span>
                  {t.error && <div className="text-[10px] text-[#475569] mt-1">{t.error}</div>}
                </td>
                <td className="px-4 py-3 text-[#475569]">{fmtDate(t.sent_at, true)}</td>
                <td className="px-4 py-3 text-right">
                  {t.status === "PENDING" && (
                    <button onClick={() => cancel(t.id)} className="text-xs text-[#DC2626] hover:underline inline-flex items-center gap-1" data-testid={`auto-cancel-${t.id}`}>
                      <Ban size={12}/> Cancel
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-8 bg-white border border-[#E2DCD0] p-5">
        <h3 className="font-display text-lg text-[#071A33]">Sequence overview</h3>
        <ul className="mt-3 text-sm text-[#334155] grid sm:grid-cols-5 gap-3">
          {[
            { s: 1, d: "+30m",  c: "Email",  p: "Soft sell" },
            { s: 2, d: "+2h",   c: "SMS",    p: "Nudge" },
            { s: 3, d: "+24h",  c: "Email",  p: "Strong" },
            { s: 4, d: "+48h",  c: "SMS",    p: "Urgency" },
            { s: 5, d: "+72h",  c: "Email",  p: "Final" },
          ].map((x) => (
            <li key={x.s} className="bg-[#F7F4ED] border border-[#E2DCD0] p-3">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#B88A2A] font-medium">Step {x.s} · {x.d}</p>
              <p className="text-[#071A33] font-medium mt-0.5">{x.c}</p>
              <p className="text-[#475569] text-xs">{x.p}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
